/**
 * External Imports
 */
import { Dispatch, FC, SetStateAction, useEffect, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

/**
 * External Imports
 */
import CSRNListTable from '../../../components/customTables/support/cscListTable';
import SearchHeader from '../../../components/searchHeader';
import SupportSideNav from '../../../components/sideNav/SupportSideNav';

import { RootState } from '../../../redux/store';
import { getCSCSubjectsToRoundForList } from '../../../redux/actions';

/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  Employee,
  RoundHistory
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import { SupportContainer } from '../styles/Support.styles';
import { date, onShiftFilter } from '../../../utils/helpers';

export interface CSRNListProps {
  authorizedUser?: AuthUserProps;
  cscSubjectsToRound: (Employee & RoundHistory)[];
  selectedDeptsFromStore: string[];
  employeeShiftStatusFilter: string;
  isShiftStatusDataAvailable: boolean;
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}

const CSRNList: FC<CSRNListProps> = ({
  authorizedUser,
  cscSubjectsToRound = [],
  employeeShiftStatusFilter,
  isShiftStatusDataAvailable,
  selectedDeptsFromStore,
  searchTerm,
  setSearchTerm
}) => {
  const dispatch = useDispatch();

  const { csrnFilter = 'all' } = useParams();

  useEffect(() => {
    if (authorizedUser?.hcaid)
      dispatch(
        getCSCSubjectsToRoundForList({
          hcaid: authorizedUser.hcaid
        })
      );
  }, []);

  const filteredCSCRounds = useMemo(() => {
    const isFilterOnShift = onShiftFilter(employeeShiftStatusFilter);
    let cscRoundsFilteredByDept = cscSubjectsToRound.filter(employee => {
      const employeeDept =
        (employee.adHoc ? employee?.employeeDept : employee?.dept) || 'Unknown';
      return selectedDeptsFromStore.some(
        selectedDept => selectedDept === employeeDept
      );
    });

    if (employeeShiftStatusFilter && employeeShiftStatusFilter !== 'all') {
      cscRoundsFilteredByDept = cscRoundsFilteredByDept.filter(
        employee => employee.onShift === isFilterOnShift
      );
    }

    return cscRoundsFilteredByDept.reduce(
      (
        acc: {
          cscSubjects: (Employee & RoundHistory)[];
          adHoc: (Employee & RoundHistory)[];
        },
        employee: Employee & RoundHistory
      ) =>
        employee.adHoc
          ? { ...acc, adHoc: [...acc.adHoc, employee] }
          : { ...acc, cscSubjects: [...acc.cscSubjects, employee] },
      { cscSubjects: [], adHoc: [] }
    );
  }, [cscSubjectsToRound, selectedDeptsFromStore, employeeShiftStatusFilter]);

  const noRoundCount = useMemo(() => {
    return filteredCSCRounds.cscSubjects.filter(
      employee =>
        !employee?.lastRoundDate ||
        (employee?.lastRoundDate &&
          date.isOverInDays(employee.lastRoundDate, 30))
    ).length;
  }, [filteredCSCRounds, cscSubjectsToRound]);
  const recentlyCompletedCount = useMemo(() => {
    return (
      filteredCSCRounds.cscSubjects.filter(
        employee =>
          employee?.lastRoundDate &&
          !date.isOverInWeeks(employee.lastRoundDate, 2)
      ).length +
      filteredCSCRounds.adHoc.filter(
        employee =>
          employee?.lastRoundDate &&
          !date.isOverInWeeks(employee.lastRoundDate, 2)
      ).length
    );
  }, [filteredCSCRounds, cscSubjectsToRound]);
  const newNursesCount = useMemo(() => {
    return filteredCSCRounds.cscSubjects.filter(subject =>
      subject.subjectClassifications?.find(
        classification => classification === 'NewNurse'
      )
    ).length;
  }, [filteredCSCRounds, cscSubjectsToRound]);
  const newToHCACount = useMemo(() => {
    return filteredCSCRounds.cscSubjects.filter(subject =>
      subject.subjectClassifications?.find(
        classification => classification === 'NewToHCA'
      )
    ).length;
  }, [filteredCSCRounds, cscSubjectsToRound]);
  const newToDeptCount = useMemo(() => {
    return filteredCSCRounds.cscSubjects.filter(subject =>
      subject.subjectClassifications?.find(
        classification => classification === 'NewToDepartment'
      )
    ).length;
  }, [filteredCSCRounds, cscSubjectsToRound]);

  return (
    <SupportContainer id="Support-Container">
      <SupportSideNav
        type="csrn"
        total={noRoundCount + recentlyCompletedCount}
        noRoundCount={noRoundCount}
        recentlyCompletedCount={recentlyCompletedCount}
        newNursesCount={newNursesCount}
        newToHCACount={newToHCACount}
        newToDeptCount={newToDeptCount}
      />
      <div className="d-flex flex-column w-100">
        <SearchHeader
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          isShiftStatusDataAvailable={isShiftStatusDataAvailable}
        />
        <CSRNListTable
          csrnFilter={csrnFilter}
          searchTerm={searchTerm}
          csrnRounds={filteredCSCRounds.cscSubjects}
          adhocRounds={filteredCSCRounds.adHoc}
        />
      </div>
    </SupportContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser,
    cscSubjectsToRound: state.EmployeeReducer.cscSubjectsToRoundForList,
    employeeShiftStatusFilter: state.EmployeeReducer.shiftStatusFilter,
    isShiftStatusDataAvailable:
      state.EmployeeReducer.isShiftStatusDataAvailable,
    selectedDeptsFromStore: state.EmployeeReducer.selectedDepts
  };
};

export default connect(mapReduxStateToProps)(CSRNList);
